/**
 * DisclosuresHome — Tier-3 Disclosure Engine landing.
 *
 * M4 (per `docs/port-design/tier-3-disclosure-engine-plan.md`). Authoring
 * workbench lives in the Risk Mgmt portal (Bible decision #1 + tier-3 plan
 * decision #1).
 *
 * Adds a Quick Stats strip with live counts pulled from
 * DisclosuresContext: # active templates, # in-review versions, # variants,
 * # merge-field tokens, # workflow links.
 */

import { useMemo } from 'react';
import { Link } from 'react-router';
import {
  FileText,
  Layers,
  GitBranch,
  Variable,
  Workflow,
  Inbox,
  RefreshCw,
} from '@retail-ops/panel-shell';

import { useDisclosuresConfig } from '@/contexts/DisclosuresContext';

export function DisclosuresHome(): JSX.Element {
  const { envelope, state, error, refresh, lastLoadedAt } = useDisclosuresConfig();

  const counts = useMemo(() => {
    const activeTemplates = envelope.templates.filter((t) => t.isActive !== false).length;
    const inReviewVersions = envelope.versions.filter(
      (v) => String((v as Record<string, unknown>)['status'] ?? '') === 'InReview',
    ).length;
    const activeVersions = envelope.versions.filter(
      (v) => String((v as Record<string, unknown>)['status'] ?? '') === 'Active',
    ).length;
    const draftVersions = envelope.versions.filter(
      (v) => String((v as Record<string, unknown>)['status'] ?? '') === 'Draft',
    ).length;
    const linksRaw = (envelope as unknown as Record<string, unknown>)['workflowLinks'];
    const workflowLinks = Array.isArray(linksRaw) ? linksRaw.length : 0;
    return {
      templates: envelope.templates.length,
      activeTemplates,
      versions: envelope.versions.length,
      inReviewVersions,
      activeVersions,
      draftVersions,
      sections: envelope.sections.length,
      variants: envelope.variants.length,
      mergeFieldGroups: envelope.mergeFieldGroups.length,
      mergeFieldDefinitions: envelope.mergeFieldDefinitions.length,
      workflowLinks,
    };
  }, [envelope]);

  return (
    <>
      <nav className="back-link-row" aria-label="breadcrumb">
        <Link to="/" className="back-link">Home</Link>
        <span aria-hidden="true"> / </span>
        <span>Disclosure Engine</span>
      </nav>

      <section className="page-header">
        <h1 className="page-header__title">Disclosure Engine</h1>
        <p className="page-header__subtitle page-intro">
          Tier-3 authoring workbench for ROP disclosure templates, versions,
          variants, and the merge-field catalog. Replaces the SPFx-side
          DisclosureEngine + hardcoded MergeFieldRegistry.
        </p>
      </section>

      <section className="counts-strip" aria-label="Quick stats">
        <CountCard
          label="Active templates"
          value={state === 'ok' ? counts.activeTemplates : null}
          sub={
            state === 'ok'
              ? `${counts.templates - counts.activeTemplates} inactive`
              : undefined
          }
        />
        <CountCard
          label="In review"
          value={state === 'ok' ? counts.inReviewVersions : null}
          sub={
            state === 'ok'
              ? `${counts.draftVersions} drafts · ${counts.activeVersions} active`
              : undefined
          }
        />
        <CountCard
          label="Variants"
          value={state === 'ok' ? counts.variants : null}
        />
        <CountCard
          label="Merge tokens"
          value={state === 'ok' ? counts.mergeFieldDefinitions : null}
          sub={state === 'ok' ? `${counts.mergeFieldGroups} groups` : undefined}
        />
        <CountCard
          label="Workflow links"
          value={state === 'ok' ? counts.workflowLinks : null}
        />
      </section>

      {error && (
        <div className="banner banner--err" role="alert">
          {error}
        </div>
      )}

      <header className="card-header">
        <div>
          <h2>Catalog summary</h2>
          {state === 'ok' && (
            <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>
              {counts.templates} templates · {counts.versions} versions ·{' '}
              {counts.variants} variants · {counts.mergeFieldDefinitions} merge fields
              {lastLoadedAt && (
                <> · refreshed {lastLoadedAt.toLocaleTimeString()}</>
              )}
            </span>
          )}
        </div>
        <div className="toolbar__spacer" style={{ flex: 1 }} />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void refresh()}
          disabled={state === 'loading'}
        >
          <RefreshCw size={14} />{' '}
          {state === 'loading' ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      <section className="tile-grid" aria-label="Disclosure Engine surfaces" data-tour="disc-tiles">
        <Tile
          to="/disclosures/templates"
          icon={<FileText size={20} />}
          title="Templates"
          body="Browse, filter, and edit disclosure templates. Layer / Audience / Multiplicity / SignatureRequirement metadata + scoping columns."
          badge={state === 'ok' ? counts.templates : null}
        />
        <Tile
          to="/disclosures/variants"
          icon={<Layers size={20} />}
          title="Variants"
          body="Per-customer-type variant rows. Selector DSL + IsActive flag drive runtime resolution at render time."
          badge={state === 'ok' ? counts.variants : null}
        />
        <Tile
          to="/disclosures/sections"
          icon={<Layers size={20} />}
          title="Sections"
          body="Reusable body fragments. Authored once and embedded into multiple disclosure bodies via include directives."
          badge={state === 'ok' ? counts.sections : null}
        />
        <Tile
          to="/disclosures/merge-fields"
          icon={<Variable size={20} />}
          title="Merge field catalog"
          body="Source-of-truth for {{token}} vocabulary. Groups + definitions + formatters."
          badge={state === 'ok' ? counts.mergeFieldDefinitions : null}
        />
        <Tile
          to="/disclosures/templates"
          icon={<GitBranch size={20} />}
          title="Versioning"
          body="Each template has multiple versions with effective-from/to ranges. Drill into a template to manage versions and section bodies."
          badge={state === 'ok' ? counts.versions : null}
        />
        <Tile
          to="/disclosures/workflow-links"
          icon={<Workflow size={20} />}
          title="Workflow links"
          body="(WorkflowCode, StepCode) → Templates[] matrix. Determines which disclosures attach at each pipeline step."
          badge={state === 'ok' ? counts.workflowLinks : null}
        />
        <Tile
          to="/disclosures/approval-queue"
          icon={<Inbox size={20} />}
          title="Approval queue"
          body="DisclosureManager triage. Review InReview drafts, approve or reject with a reason. (Wave 4 Agent E.)"
          badge={state === 'ok' ? counts.inReviewVersions : null}
        />
      </section>

      <footer className="list-footer" style={{ marginTop: 16 }}>
        <span style={{ color: 'var(--ink-500)' }}>
          Tier-3 Disclosure Engine · Risk Mgmt authoring workbench (M4 live).
        </span>
      </footer>
    </>
  );
}

function CountCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: number | null;
  sub?: string;
}): JSX.Element {
  return (
    <div className="counts-strip__item">
      <div className="counts-strip__label">{label}</div>
      <div className="counts-strip__value">{value == null ? '—' : value}</div>
      {sub && (
        <div style={{ fontSize: 10, color: 'var(--ink-500)', marginTop: 2 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function Tile({
  to,
  icon,
  title,
  body,
  badge,
}: {
  to: string;
  icon: JSX.Element;
  title: string;
  body: string;
  badge?: number | null;
}): JSX.Element {
  return (
    <Link className="tile" to={to}>
      <div
        className="tile__icon"
        aria-hidden="true"
        style={{ position: 'relative' }}
      >
        {icon}
        {badge != null && badge > 0 && (
          <span
            className="pill pill--info"
            style={{
              position: 'absolute',
              top: -8,
              right: -10,
              fontSize: 10,
              padding: '2px 6px',
              minWidth: 18,
              justifyContent: 'center',
            }}
          >
            {badge}
          </span>
        )}
      </div>
      <div className="tile__title">{title}</div>
      <div className="tile__body">{body}</div>
    </Link>
  );
}
